import React, { useState, useEffect, useRef } from 'react';
import { Avatar, Tooltip } from 'antd';
import { RobotOutlined, CheckCircleOutlined } from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import MessageActions from './MessageActions';
import InlineSourceChip from '../sources/InlineSourceChip';
import StreamingText from '../common/StreamingText';
import styles from '../../styles/chatV2.module.css';

const AIMessage = ({
  message,
  isStreaming = false,
  onRegenerate,
  onFeedback,
  onCopy,
}) => {
  const [showActions, setShowActions] = useState(false);
  const messageRef = useRef(null);
  const [hasScrolled, setHasScrolled] = useState(false);

  // Auto-scroll to message while streaming
  useEffect(() => {
    if (isStreaming && !hasScrolled && messageRef.current) {
      messageRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
      setHasScrolled(true);
    }
  }, [isStreaming, message.content, hasScrolled]);

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  return (
    <div
      ref={messageRef}
      className={styles.messageWrapper}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      {/* AI Avatar */}
      <div className={styles.avatarContainer}>
        <Avatar
          size={36}
          icon={<RobotOutlined />}
          className={styles.aiAvatar}
        />
        {!isStreaming && (
          <Tooltip title="Response complete">
            <CheckCircleOutlined className={styles.completedBadge} />
          </Tooltip>
        )}
      </div>

      {/* Message Content */}
      <div className={styles.messageContent}>
        <div className={styles.messageBubble}>
          {isStreaming ? (
            <StreamingText text={message.content} speed={20} />
          ) : (
            <div className={styles.markdown}>
              <ReactMarkdown
                components={{
                  code: ({ node, inline, className, children, ...props }) => (
                    <code
                      className={`${className || ''} ${
                        inline ? styles.inlineCode : styles.codeBlock
                      }`}
                      {...props}
                    >
                      {children}
                    </code>
                  ),
                  a: ({ node, children, href, ...props }) => (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.markdownLink}
                      {...props}
                    >
                      {children}
                    </a>
                  ),
                }}
              >
                {message.content}
              </ReactMarkdown>
            </div>
          )}

          {/* Inline Source Citations */}
          {message.sources && message.sources.length > 0 && (
            <div className={styles.inlineSources}>
              <div className={styles.sourcesLabel}>Sources:</div>
              <div className={styles.sourceChips}>
                {message.sources.map((source, index) => (
                  <InlineSourceChip
                    key={source.sourceId || index}
                    source={source}
                    index={index + 1}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Timestamp & Actions */}
        <div className={styles.messageFooter}>
          <span className={styles.timestamp}>
            {formatTimestamp(message.timestamp)}
          </span>

          {showActions && !isStreaming && (
            <MessageActions
              message={message}
              onRegenerate={onRegenerate}
              onFeedback={onFeedback}
              onCopy={onCopy}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AIMessage;
